import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Row } from 'react-table';

import type { SetDrawerProps } from '../../../../common/Drawer/useDrawerProps';
import { openToastAlert } from '../../../../common/ToastAlert/toastAlert';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';
import type { FilterOptionsInput } from '../../utils';
import { usePendingLineItemDrawer } from '../contexts';
import type { LineItemModel } from '../modelConverters';
import type { TraffickingPageDrawerData } from './makePageDrawerPlugin';
import { DrawerType, EntityType } from './makePageDrawerPlugin';
import type { SelectedRowIds } from './useTraffickerState';
import { useVerifyLineItemInFilters } from './useVerifyLineItemInFilters';

interface UseOpenDrawerOnNavigationOptions {
  rows: Row<LineItemModel>[];
  initialLoading: boolean;
  setDrawerProps: SetDrawerProps<TraffickingPageDrawerData>;
  setLineItemIdsToQuery: Dispatch<SetStateAction<SelectedRowIds>>;
  lineItemFilterOptions: FilterOptionsInput;
  onPinLineItem: (lineItemId: string) => void;
  toggleRowSelected: (rowId: string, set?: boolean) => void;
}

interface UseOpenDrawerOnNavigationResult {
  scrollTargetId: string | null;
  clearScrollTarget: () => void;
}

function useOpenDrawerOnNavigation({
  rows,
  initialLoading,
  setDrawerProps,
  setLineItemIdsToQuery,
  lineItemFilterOptions,
  onPinLineItem,
  toggleRowSelected,
}: UseOpenDrawerOnNavigationOptions): UseOpenDrawerOnNavigationResult {
  const { adsVsmLineItemNavigationLinks } = useFeatureFlags();
  const { pendingLineItemId, setPendingLineItemId } = usePendingLineItemDrawer();
  const [scrollTargetId, setScrollTargetId] = useState<string | null>(null);
  const [awaitingFetch, setAwaitingFetch] = useState(false);

  const { verifyLineItemInFilters, verificationTotal, verificationLoading } = useVerifyLineItemInFilters();
  const hasVerifiedRef = useRef(false);
  const justDispatchedRef = useRef(false);

  // Case 1: Target row is already in rows — open drawer + scroll
  useEffect(() => {
    if (!adsVsmLineItemNavigationLinks) return;
    if (!pendingLineItemId) return;
    if (initialLoading) return;

    const targetRow = rows.find((row) => row.id === pendingLineItemId);

    if (targetRow) {
      onPinLineItem(pendingLineItemId);
      toggleRowSelected(pendingLineItemId, true);
      setDrawerProps({
        data: {
          drawerType: DrawerType.ENTITY_DETAILS,
          dataType: EntityType.LINE_ITEM,
          ...targetRow.original,
        },
        isOpen: true,
      });
      setScrollTargetId(pendingLineItemId);
      setPendingLineItemId(null);
      setAwaitingFetch(false);
      hasVerifiedRef.current = false;
      justDispatchedRef.current = false;
    }
  }, [
    adsVsmLineItemNavigationLinks,
    rows,
    initialLoading,
    pendingLineItemId,
    setPendingLineItemId,
    setDrawerProps,
    onPinLineItem,
    toggleRowSelected,
  ]);

  // Fire verification query when target is not in rows
  useEffect(() => {
    if (!adsVsmLineItemNavigationLinks) return;
    if (!pendingLineItemId) return;
    if (initialLoading) return;
    if (hasVerifiedRef.current) return;
    if (awaitingFetch) return;

    const targetRow = rows.find((row) => row.id === pendingLineItemId);
    if (targetRow) return;

    hasVerifiedRef.current = true;
    justDispatchedRef.current = true;
    verifyLineItemInFilters(pendingLineItemId, lineItemFilterOptions);
  }, [
    adsVsmLineItemNavigationLinks,
    pendingLineItemId,
    initialLoading,
    rows,
    awaitingFetch,
    verifyLineItemInFilters,
    lineItemFilterOptions,
  ]);

  // Handle verification result
  useEffect(() => {
    if (!pendingLineItemId) return;
    if (!hasVerifiedRef.current) return;
    if (justDispatchedRef.current) {
      justDispatchedRef.current = false;
      return;
    }
    if (verificationLoading) return;
    if (verificationTotal === null) return;
    if (awaitingFetch) return;

    if (verificationTotal >= 1) {
      // Case 2: Item passes filters — fetch it via selectedIdList, wait for it to appear
      setLineItemIdsToQuery((prev) => ({ ...prev, [pendingLineItemId]: true }));
      setAwaitingFetch(true);
    } else {
      // Case 3: Item filtered out — pin selection but show toast
      onPinLineItem(pendingLineItemId);
      openToastAlert({
        alertType: 'warning',
        message: 'Line item hidden by filters',
        description:
          'The selected line item is not visible because it is excluded by the active filters. Clear filters to view the selected line item.',
      });
      setPendingLineItemId(null);
      hasVerifiedRef.current = false;
    }
  }, [
    pendingLineItemId,
    verificationTotal,
    verificationLoading,
    awaitingFetch,
    setLineItemIdsToQuery,
    setPendingLineItemId,
    onPinLineItem,
  ]);

  // Reset state when pendingLineItemId is cleared externally
  useEffect(() => {
    if (!pendingLineItemId) {
      hasVerifiedRef.current = false;
      justDispatchedRef.current = false;
      setAwaitingFetch(false);
    }
  }, [pendingLineItemId]);

  const clearScrollTarget = (): void => setScrollTargetId(null);

  return { scrollTargetId, clearScrollTarget };
}

export default useOpenDrawerOnNavigation;
